import React, { useEffect, useState } from 'react';
import { useWorkoutsContext } from '../hooks/useWorkoutsContext';

// Components
import WorkoutDetails from '../components/WorkoutDetails';
import WorkoutForm from '../components/WorkoutForm';
import StatsDashboard from '../components/StatsDashboard';
import AnalyticsCharts from '../components/AnalyticsCharts';
import ControlsPanel from '../components/ControlsPanel';

const Home = () => {
  const { workouts, dispatch } = useWorkoutsContext();
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Form & Interaction states
  const [editWorkout, setEditWorkout] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sortBy, setSortBy] = useState('newest');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [toasts, setToasts] = useState([]);

  // Toast Notification handler
  const showToast = (message, type = 'success') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, message, type }]);
    
    // Auto-remove after 3.5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  };

  // Fetch all workouts from backend on mount
  useEffect(() => {
    const fetchWorkouts = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workouts`);
        const json = await response.json();

        if (response.ok) {
          dispatch({ type: 'SET_WORKOUT', payload: json });
          setFetchError(null);
        } else {
          setFetchError(json.error || 'Could not fetch workouts');
        }
      } catch (err) {
        setFetchError('Connection refused. Is the server running?');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkouts();
  }, [dispatch]);

  // Handle item sorting & filtering
  const filteredAndSortedWorkouts = workouts
    ? workouts
        .filter((w) => {
          const matchesCategory = activeCategory === 'All' || (w.category || 'Strength') === activeCategory;
          const matchesSearch = w.title.toLowerCase().includes(searchQuery.toLowerCase());
          return matchesCategory && matchesSearch;
        })
        .sort((a, b) => {
          // Always keep favorites/pinned items at the absolute top
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;

          // Secondary sorting
          if (sortBy === 'newest') {
            return new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt);
          }
          if (sortBy === 'oldest') {
            return new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt);
          }
          if (sortBy === 'highest-load') {
            // Normalize weights to lbs for accurate sorting
            const loadA = a.weightUnit === 'kg' ? a.load * 2.20462 : a.load;
            const loadB = b.weightUnit === 'kg' ? b.load * 2.20462 : b.load;
            return loadB - loadA;
          }
          if (sortBy === 'highest-reps') {
            return b.reps - a.reps;
          }
          return 0;
        })
    : [];

  return (
    <div className="home-container">
      {/* 1. Dashboard Metrics Summary */}
      <StatsDashboard workouts={workouts} activeUnit={weightUnit} />

      {/* 2. Visual Charts (Donut + Bars) */}
      <AnalyticsCharts workouts={workouts} />

      {/* 3. Filtering and Control bar */}
      <ControlsPanel
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        weightUnit={weightUnit}
        setWeightUnit={setWeightUnit}
      />

      {/* 4. Workouts List and Input Form split */}
      <div className="home">
        <div className="workouts-container">
          {isLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              Loading your workout log...
            </div>
          )}

          {fetchError && (
            <div className="error" style={{ textAlign: 'center', margin: '20px 0' }}>
              {fetchError}
            </div>
          )}

          {!isLoading && !fetchError && filteredAndSortedWorkouts.length === 0 && (
            <div className="no-workouts">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              <p>No workouts found matching the filters.</p>
              <p style={{ fontSize: '0.8rem', marginTop: '6px' }}>Try changing filters or add a new exercise!</p>
            </div>
          )}

          {!isLoading &&
            !fetchError &&
            filteredAndSortedWorkouts.map((workout) => (
              <WorkoutDetails
                key={workout._id}
                workout={workout}
                activeUnit={weightUnit}
                onEditClick={setEditWorkout}
                onShowToast={showToast}
              />
            ))}
        </div>

        <WorkoutForm
          editWorkout={editWorkout}
          onCancelEdit={() => setEditWorkout(null)}
          onShowToast={showToast}
        />
      </div>

      {/* 5. Custom Toast Notifications Container */}
      <div className="toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            <div className={`toast-icon ${toast.type}`}>
              {toast.type === 'success' && (
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              )}
              {toast.type === 'error' && (
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              )}
              {toast.type === 'info' && (
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                </svg>
              )}
            </div>
            <div className="toast-message">{toast.message}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Home;